/**
 * Birds of Silk — v0.2 web client entry.
 *
 * Boots Pixi, fetches the daily descriptor, runs the deterministic round,
 * drives the HUD (score + timer + food picker), and posts a completion event.
 */

import { Application } from 'pixi.js';
import { fetchToday, loadDayMeta } from './data/loader';
import {
  dismissInstructions,
  getDisplayName,
  getOrCreateDeviceId,
  instructionsDismissed,
  isFirstAttemptScored,
  markFirstAttemptScored,
  setDisplayName,
} from './data/storage';
import { postCompletion } from './data/completion';
import { Round } from './sim/round';
import { SIM_DT, SIM_HZ } from './sim/constants';
import { RoundRenderer } from './render/renderer';

const $ = <T extends HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing element #${id}`);
  return el as T;
};

const FOOD_DISPLAY: Record<string, { label: string; swatch: string }> = {
  millet:    { label: 'Millet',    swatch: '#e9d6a3' },
  berries:   { label: 'Berries',   swatch: '#c6395c' },
  sunflower: { label: 'Sunflower', swatch: '#3a2a1a' },
};

function formatTime(seconds: number): string {
  const total = Math.max(0, Math.ceil(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

async function main(): Promise<void> {
  const deviceId = getOrCreateDeviceId();

  const instructionsModal = $('instructions-modal');
  const dismissBtn = $<HTMLButtonElement>('instructions-dismiss');
  if (!instructionsDismissed()) instructionsModal.classList.remove('hidden');
  dismissBtn.addEventListener('click', () => {
    instructionsModal.classList.add('hidden');
    dismissInstructions();
  });
  $('help-button').addEventListener('click', () => {
    instructionsModal.classList.remove('hidden');
  });

  const app = new Application();
  await app.init({
    background: '#0a0a0a',
    resizeTo: window,
    antialias: true,
    autoDensity: true,
    resolution: window.devicePixelRatio,
  });
  $('stage').appendChild(app.canvas);

  const renderer = new RoundRenderer(app);
  window.addEventListener('resize', () => renderer.resize());

  const today = await fetchToday();
  const meta = await loadDayMeta(today.bundleUrl);
  await renderer.loadDay(meta, today.bundleUrl);

  const wx = meta.weather;
  $('weather-strip').textContent =
    `${meta.title} · ${wx.light_label} · ${wx.temperature_label} · ${wx.icons.join(' · ')}`;

  const round = new Round(meta, app.renderer.width, app.renderer.height);

  // --- Food picker UI ---
  let selectedFood: string = meta.pouch[0]?.food_id ?? 'millet';
  const picker = $('food-picker');
  picker.innerHTML = '';
  const foodButtons = new Map<string, HTMLButtonElement>();
  for (const p of meta.pouch) {
    const meta2 = FOOD_DISPLAY[p.food_id] ?? { label: p.food_id, swatch: '#888' };
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'food-btn' + (p.food_id === selectedFood ? ' selected' : '');
    btn.dataset.foodId = p.food_id;
    btn.innerHTML = `
      <span class="food-swatch" style="background:${meta2.swatch}"></span>
      <span class="food-count">${p.count}</span>
      <span class="food-name">${meta2.label}</span>
    `;
    btn.addEventListener('click', (ev) => {
      ev.stopPropagation();
      selectedFood = p.food_id;
      for (const [, b] of foodButtons) b.classList.remove('selected');
      btn.classList.add('selected');
    });
    picker.appendChild(btn);
    foodButtons.set(p.food_id, btn);
  }

  // --- Tap to scatter food ---
  // Pointerdown on the canvas only (not on HUD buttons, which stop propagation).
  app.canvas.addEventListener('pointerdown', (ev: PointerEvent) => {
    if (round.state.phase !== 'play') return;
    const remaining = round.state.pouch.get(selectedFood) ?? 0;
    if (remaining <= 0) return;
    const rect = app.canvas.getBoundingClientRect();
    const x = ev.clientX - rect.left;
    const y = ev.clientY - rect.top;
    round.queueInput({ tick: round.state.tick, x, y, foodId: selectedFood });
  });

  // --- HUD elements ---
  const hudTimer = $('hud-timer');
  const hudScore = $('hud-score');

  // --- Main loop: fixed-step sim + interpolated render + HUD refresh ---
  let acc = 0;
  let last = performance.now();
  let ended = false;
  let lastHudRefresh = 0;
  app.ticker.add(() => {
    const now = performance.now();
    const dt = Math.min(0.25, (now - last) / 1000);
    last = now;
    acc += dt;
    while (acc >= SIM_DT) {
      round.step();
      acc -= SIM_DT;
    }
    renderer.render(round);

    // HUD ~10 Hz
    if (now - lastHudRefresh > 100) {
      lastHudRefresh = now;
      hudTimer.textContent = formatTime(round.secondsRemaining());
      hudTimer.classList.toggle('warn', round.secondsRemaining() < 15 && round.state.phase === 'play');
      hudScore.textContent = String(round.liveScore());
      for (const [foodId, btn] of foodButtons) {
        const remaining = round.state.pouch.get(foodId) ?? 0;
        const countEl = btn.querySelector('.food-count');
        if (countEl) countEl.textContent = String(remaining);
        btn.classList.toggle('empty', remaining === 0);
        if (remaining === 0 && foodId === selectedFood) {
          // Auto-switch to any food that still has stock.
          for (const [otherId, otherBtn] of foodButtons) {
            if ((round.state.pouch.get(otherId) ?? 0) > 0) {
              selectedFood = otherId;
              for (const [, b] of foodButtons) b.classList.remove('selected');
              otherBtn.classList.add('selected');
              break;
            }
          }
        }
      }
    }

    if (round.state.phase === 'ended' && !ended) {
      ended = true;
      onRoundEnded(today.id, round.state.score, deviceId);
    }
  });

  console.info(`[bos] booted day=${meta.id} sim=${SIM_HZ}Hz device=${deviceId.slice(0, 8)}…`);
}

async function onRoundEnded(dayId: string, score: number, deviceId: string): Promise<void> {
  const isFirst = !isFirstAttemptScored(dayId);
  if (isFirst) markFirstAttemptScored(dayId);

  let name = getDisplayName();
  if (!name) {
    name = await promptForName();
    setDisplayName(name);
  }

  postCompletion({
    deviceId,
    displayName: name,
    dayId,
    score,
    completedAt: new Date().toISOString(),
    isFirstAttempt: isFirst,
  });

  $('end-score').textContent = String(score);
  $('end-panel').classList.remove('hidden');
  $<HTMLButtonElement>('btn-replay').addEventListener('click', () => {
    location.reload();
  });
}

function promptForName(): Promise<string> {
  return new Promise((resolve) => {
    const modal = $('name-modal');
    const input = $<HTMLInputElement>('name-input');
    const submit = $<HTMLButtonElement>('name-submit');
    modal.classList.remove('hidden');
    input.focus();
    const finish = (): void => {
      const v = input.value.trim().slice(0, 20) || 'Guest';
      modal.classList.add('hidden');
      resolve(v);
    };
    submit.addEventListener('click', finish, { once: true });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') finish();
    });
  });
}

void main().catch((err) => {
  console.error('[bos] fatal', err);
});
